/**
 * Truss Tool - BIM 2025 Grade
 * 
 * Structural truss creation with full complexity:
 * - Multiple truss profiles (Howe, Pratt, Warren, Fink, etc.)
 * - Configurable panel count and heights
 * - Top/Bottom chord properties
 * - Web member configurations
 * - Bearing conditions
 * - Individual member scheduling
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, StructuralMaterial, AnalyticalModel, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Truss Types ====================

export type TrussProfile =
    | 'Howe'            // Verticals/diagonals: diagonals slope up toward center
    | 'Pratt'           // Verticals/diagonals: diagonals slope down toward center
    | 'Warren'          // Only diagonals (no verticals)
    | 'WarrenWithVerticals'
    | 'Fink'            // W-pattern diagonals
    | 'KingPost'        // Single center vertical
    | 'QueenPost'       // Two verticals
    | 'Scissors'        // Crossed bottom chords
    | 'Bowstring'       // Curved top chord
    | 'FlatTop';        // Flat top chord

export type TrussChordProfile =
    | 'WShape'          // W-section
    | 'LShape'          // Angle
    | 'Tube'            // Hollow section
    | 'Pipe'            // Circular hollow
    | 'Channel'
    | 'Timber';

// ==================== Member Properties ====================

export interface TrussMemberProfile {
    shape: TrussChordProfile;
    size: string;           // e.g., "W310x97", "L100x100x10", "HSS100x100x6"
    width: number;          // mm
    height: number;         // mm
    thickness?: number;     // mm for hollow sections
}

export interface TrussChordConfig {
    profile: TrussMemberProfile;
    isContinuous: boolean;  // Single member or spliced
    splice?: {
        locations: number[];    // Fraction of span (0-1) where splices occur
        type: 'Bolted' | 'Welded';
    };
}

export interface TrussWebConfig {
    profile: TrussMemberProfile;
    connectionType: 'Gusset' | 'Welded' | 'Bolted';
}

// ==================== Truss Geometry ====================

export interface TrussMember {
    id: string;
    type: 'TopChord' | 'BottomChord' | 'Vertical' | 'Diagonal';
    start: [number, number, number];
    end: [number, number, number];
    profile: TrussMemberProfile;
    length: number;         // m
    force?: number;         // kN (positive = tension, negative = compression)
}

export interface EnhancedTrussGeometryData {
    // Baseline
    start: [number, number, number];
    end: [number, number, number];

    // Profile type
    trussProfile: TrussProfile;

    // Dimensions
    span: number;           // m
    height: number;         // m - Height at center
    startHeight: number;    // m - Height at left support
    endHeight: number;      // m - Height at right support

    // Panel configuration
    panelCount: number;     // Number of panels
    panelWidth: number;     // m - Width of each panel

    // Chord configuration
    topChord: TrussChordConfig;
    bottomChord: TrussChordConfig;
    webConfig: TrussWebConfig;

    // Level constraint
    bearingLevelId: string;
    bearingOffset: number;  // mm

    // Generated members
    members: TrussMember[];

    // Calculated
    totalWeight: number;    // kg
    topChordLength: number; // m
    bottomChordLength: number; // m
}

// ==================== Enhanced Truss Element ====================

export interface EnhancedTrussElement extends Element {
    category: 'Structural Trusses';
    geometryData: EnhancedTrussGeometryData;

    // Structural
    structuralMaterial: StructuralMaterial;
    analyticalModel: AnalyticalModel;

    // Bearing
    bearingConditions: {
        start: 'Pinned' | 'Roller' | 'Fixed';
        end: 'Pinned' | 'Roller' | 'Fixed';
    };

    // Fabrication
    fabrication?: {
        mark: string;
        shippingPieces: number;
        totalWeight: number;
    };
}

// ==================== Tool Options ====================

export interface EnhancedTrussToolOptions {
    // Type
    familyName: string;
    typeName: string;
    trussProfile: TrussProfile;

    // Dimensions
    height: number;         // mm
    panelCount: number;

    // Chords
    topChordProfile: TrussMemberProfile;
    bottomChordProfile: TrussMemberProfile;
    webProfile: TrussMemberProfile;

    // Material
    materialId: string;

    // Level
    bearingLevelId: string;
    bearingOffset: number;

    // Bearing conditions
    startBearing: 'Pinned' | 'Roller' | 'Fixed';
    endBearing: 'Pinned' | 'Roller' | 'Fixed';
}

// Standard member profiles
export const STANDARD_TRUSS_PROFILES: Record<string, TrussMemberProfile> = {
    'L100x100x10': { shape: 'LShape', size: 'L100x100x10', width: 100, height: 100, thickness: 10 },
    'L75x75x8': { shape: 'LShape', size: 'L75x75x8', width: 75, height: 75, thickness: 8 },
    'HSS100x100x6': { shape: 'Tube', size: 'HSS100x100x6', width: 100, height: 100, thickness: 6 },
    'HSS76x76x5': { shape: 'Tube', size: 'HSS76x76x5', width: 76, height: 76, thickness: 5 },
    'W310x97': { shape: 'WShape', size: 'W310x97', width: 305, height: 308 },
    'W250x73': { shape: 'WShape', size: 'W250x73', width: 254, height: 254 },
    'Pipe100x6': { shape: 'Pipe', size: 'Pipe100x6', width: 100, height: 100, thickness: 6 },
    'Timber_100x200': { shape: 'Timber', size: '100x200', width: 100, height: 200 }
};

// ==================== Truss Tool Class ====================

export class TrussTool extends BaseTool {
    id = 'truss';
    name = 'Truss';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    private options: EnhancedTrussToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): EnhancedTrussToolOptions {
        return {
            familyName: 'Steel Truss',
            typeName: 'Pratt',
            trussProfile: 'Pratt',
            height: 1500,               // mm
            panelCount: 8,
            topChordProfile: STANDARD_TRUSS_PROFILES['L100x100x10'],
            bottomChordProfile: STANDARD_TRUSS_PROFILES['L100x100x10'],
            webProfile: STANDARD_TRUSS_PROFILES['L75x75x8'],
            materialId: 'mat-steel-350',
            bearingLevelId: 'Level 1',
            bearingOffset: 0,
            startBearing: 'Pinned',
            endBearing: 'Roller'
        };
    }

    /**
     * Set truss options from UI
     */
    setOptions(options: Partial<EnhancedTrussToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): EnhancedTrussToolOptions {
        return { ...this.options };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            if (snapped.distanceTo(this.startPoint) < 0.5) return;

            const truss = this.createTruss(this.startPoint, snapped);
            this.context.bimStore.addElement(truss);

            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview(): void {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'truss',
                start: this.startPoint,
                end: this.currentPoint,
                height: this.options.height / 1000,
                trussProfile: this.options.trussProfile,
                panelCount: this.options.panelCount
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createTruss(start: THREE.Vector3, end: THREE.Vector3): EnhancedTrussElement {
        const span = start.distanceTo(end);
        const height = this.options.height / 1000;
        const panelWidth = span / this.options.panelCount;

        // Generate all truss members
        const members = this.generateMembers(start, end, height, this.options.panelCount);

        // Calculate total weight
        const totalWeight = this.calculateTotalWeight(members);

        // Calculate chord lengths
        const topChordLength = members
            .filter(m => m.type === 'TopChord')
            .reduce((sum, m) => sum + m.length, 0);
        const bottomChordLength = members
            .filter(m => m.type === 'BottomChord')
            .reduce((sum, m) => sum + m.length, 0);

        const geometryData: EnhancedTrussGeometryData = {
            start: start.toArray() as [number, number, number],
            end: end.toArray() as [number, number, number],
            trussProfile: this.options.trussProfile,
            span,
            height,
            startHeight: height,
            endHeight: height,
            panelCount: this.options.panelCount,
            panelWidth,
            topChord: {
                profile: this.options.topChordProfile,
                isContinuous: true
            },
            bottomChord: {
                profile: this.options.bottomChordProfile,
                isContinuous: true
            },
            webConfig: {
                profile: this.options.webProfile,
                connectionType: 'Gusset'
            },
            bearingLevelId: this.options.bearingLevelId,
            bearingOffset: this.options.bearingOffset,
            members,
            totalWeight,
            topChordLength,
            bottomChordLength
        };

        // Create analytical model
        const analyticalModel = this.createAnalyticalModel(members);

        // Get material
        const structuralMaterial = this.getMaterial(this.options.materialId);

        const truss: EnhancedTrussElement = {
            id: uuidv4(),
            category: 'Structural Trusses',
            type: `${this.options.familyName}: ${this.options.trussProfile}`,
            parameters: this.buildParameters(geometryData),
            constraints: this.buildConstraints(),
            geometryData,
            structuralMaterial,
            analyticalModel,
            bearingConditions: {
                start: this.options.startBearing,
                end: this.options.endBearing
            },
            fabrication: {
                mark: '',
                shippingPieces: Math.ceil(span / 12), // Max 12m shipping
                totalWeight
            }
        };

        return truss;
    }

    private generateMembers(
        start: THREE.Vector3,
        end: THREE.Vector3,
        height: number,
        panelCount: number
    ): TrussMember[] {
        const members: TrussMember[] = [];
        const span = start.distanceTo(end);
        // const panelWidth = span / panelCount;

        // Direction along truss
        const dir = end.clone().sub(start).normalize();
        const up = new THREE.Vector3(0, 0, 1);

        // Generate panel points
        const bottomNodes: THREE.Vector3[] = [];
        const topNodes: THREE.Vector3[] = [];

        for (let i = 0; i <= panelCount; i++) {
            const fraction = i / panelCount;
            const bottomPt = start.clone().add(dir.clone().multiplyScalar(span * fraction));
            bottomNodes.push(bottomPt);

            // Top chord can have varying height (for pitched trusses)
            const topHeight = this.getTopChordHeight(fraction, height);
            const topPt = bottomPt.clone().add(up.clone().multiplyScalar(topHeight));
            topNodes.push(topPt);
        }

        // Bottom chord members
        for (let i = 0; i < panelCount; i++) {
            members.push({
                id: uuidv4(),
                type: 'BottomChord',
                start: bottomNodes[i].toArray() as [number, number, number],
                end: bottomNodes[i + 1].toArray() as [number, number, number],
                profile: this.options.bottomChordProfile,
                length: bottomNodes[i].distanceTo(bottomNodes[i + 1])
            });
        }

        // Top chord members
        for (let i = 0; i < panelCount; i++) {
            members.push({
                id: uuidv4(),
                type: 'TopChord',
                start: topNodes[i].toArray() as [number, number, number],
                end: topNodes[i + 1].toArray() as [number, number, number],
                profile: this.options.topChordProfile,
                length: topNodes[i].distanceTo(topNodes[i + 1])
            });
        }

        // Web members based on truss profile
        this.generateWebMembers(members, bottomNodes, topNodes, panelCount);

        return members;
    }

    private getTopChordHeight(fraction: number, height: number): number {
        // For pitched trusses, height varies
        switch (this.options.trussProfile) {
            case 'Fink':
            case 'Howe':
            case 'Pratt':
            case 'Warren':
            case 'WarrenWithVerticals':
                // Peaked at center
                return height;
            case 'Bowstring':
                // Curved (parabolic)
                return height * (1 - Math.pow(2 * fraction - 1, 2));
            case 'FlatTop':
                return height;
            default:
                return height;
        }
    }

    private generateWebMembers(
        members: TrussMember[],
        bottomNodes: THREE.Vector3[],
        topNodes: THREE.Vector3[],
        panelCount: number
    ): void {
        switch (this.options.trussProfile) {
            case 'Pratt':
                // Verticals at each panel, diagonals slope down toward center
                for (let i = 0; i <= panelCount; i++) {
                    // Verticals
                    if (i > 0 && i < panelCount) {
                        members.push({
                            id: uuidv4(),
                            type: 'Vertical',
                            start: bottomNodes[i].toArray() as [number, number, number],
                            end: topNodes[i].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: bottomNodes[i].distanceTo(topNodes[i])
                        });
                    }
                }
                // Diagonals
                for (let i = 0; i < panelCount; i++) {
                    const isLeftHalf = i < panelCount / 2;
                    if (isLeftHalf) {
                        members.push({
                            id: uuidv4(),
                            type: 'Diagonal',
                            start: bottomNodes[i].toArray() as [number, number, number],
                            end: topNodes[i + 1].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: bottomNodes[i].distanceTo(topNodes[i + 1])
                        });
                    } else {
                        members.push({
                            id: uuidv4(),
                            type: 'Diagonal',
                            start: bottomNodes[i + 1].toArray() as [number, number, number],
                            end: topNodes[i].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: bottomNodes[i + 1].distanceTo(topNodes[i])
                        });
                    }
                }
                break;

            case 'Howe':
                // Opposite of Pratt: diagonals slope up toward center
                for (let i = 0; i <= panelCount; i++) {
                    if (i > 0 && i < panelCount) {
                        members.push({
                            id: uuidv4(),
                            type: 'Vertical',
                            start: bottomNodes[i].toArray() as [number, number, number],
                            end: topNodes[i].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: bottomNodes[i].distanceTo(topNodes[i])
                        });
                    }
                }
                for (let i = 0; i < panelCount; i++) {
                    const isLeftHalf = i < panelCount / 2;
                    if (isLeftHalf) {
                        members.push({
                            id: uuidv4(),
                            type: 'Diagonal',
                            start: topNodes[i].toArray() as [number, number, number],
                            end: bottomNodes[i + 1].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: topNodes[i].distanceTo(bottomNodes[i + 1])
                        });
                    } else {
                        members.push({
                            id: uuidv4(),
                            type: 'Diagonal',
                            start: topNodes[i + 1].toArray() as [number, number, number],
                            end: bottomNodes[i].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: topNodes[i + 1].distanceTo(bottomNodes[i])
                        });
                    }
                }
                break;

            case 'Warren':
                // Only diagonals, alternating
                for (let i = 0; i < panelCount; i++) {
                    if (i % 2 === 0) {
                        members.push({
                            id: uuidv4(),
                            type: 'Diagonal',
                            start: bottomNodes[i].toArray() as [number, number, number],
                            end: topNodes[i + 1].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: bottomNodes[i].distanceTo(topNodes[i + 1])
                        });
                    } else {
                        members.push({
                            id: uuidv4(),
                            type: 'Diagonal',
                            start: topNodes[i].toArray() as [number, number, number],
                            end: bottomNodes[i + 1].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: topNodes[i].distanceTo(bottomNodes[i + 1])
                        });
                    }
                }
                break;

            case 'WarrenWithVerticals':
                // Warren pattern plus verticals
                for (let i = 1; i < panelCount; i++) {
                    members.push({
                        id: uuidv4(),
                        type: 'Vertical',
                        start: bottomNodes[i].toArray() as [number, number, number],
                        end: topNodes[i].toArray() as [number, number, number],
                        profile: this.options.webProfile,
                        length: bottomNodes[i].distanceTo(topNodes[i])
                    });
                }
                for (let i = 0; i < panelCount; i++) {
                    if (i % 2 === 0) {
                        members.push({
                            id: uuidv4(),
                            type: 'Diagonal',
                            start: bottomNodes[i].toArray() as [number, number, number],
                            end: topNodes[i + 1].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: bottomNodes[i].distanceTo(topNodes[i + 1])
                        });
                    } else {
                        members.push({
                            id: uuidv4(),
                            type: 'Diagonal',
                            start: topNodes[i].toArray() as [number, number, number],
                            end: bottomNodes[i + 1].toArray() as [number, number, number],
                            profile: this.options.webProfile,
                            length: topNodes[i].distanceTo(bottomNodes[i + 1])
                        });
                    }
                }
                break;

            case 'KingPost': {
                // Single center vertical
                const centerIdx = Math.floor(panelCount / 2);
                members.push({
                    id: uuidv4(),
                    type: 'Vertical',
                    start: bottomNodes[centerIdx].toArray() as [number, number, number],
                    end: topNodes[centerIdx].toArray() as [number, number, number],
                    profile: this.options.webProfile,
                    length: bottomNodes[centerIdx].distanceTo(topNodes[centerIdx])
                });
                // Diagonals from supports to apex
                members.push({
                    id: uuidv4(),
                    type: 'Diagonal',
                    start: bottomNodes[0].toArray() as [number, number, number],
                    end: topNodes[centerIdx].toArray() as [number, number, number],
                    profile: this.options.webProfile,
                    length: bottomNodes[0].distanceTo(topNodes[centerIdx])
                });
                members.push({
                    id: uuidv4(),
                    type: 'Diagonal',
                    start: bottomNodes[panelCount].toArray() as [number, number, number],
                    end: topNodes[centerIdx].toArray() as [number, number, number],
                    profile: this.options.webProfile,
                    length: bottomNodes[panelCount].distanceTo(topNodes[centerIdx])
                });
                break;
            }

            default:
                // Default Warren pattern
                for (let i = 0; i < panelCount; i++) {
                    members.push({
                        id: uuidv4(),
                        type: 'Diagonal',
                        start: bottomNodes[i].toArray() as [number, number, number],
                        end: topNodes[i + 1].toArray() as [number, number, number],
                        profile: this.options.webProfile,
                        length: bottomNodes[i].distanceTo(topNodes[i + 1])
                    });
                }
        }
    }

    private calculateTotalWeight(members: TrussMember[]): number {
        const material = this.getMaterial(this.options.materialId);
        let totalWeight = 0;

        for (const member of members) {
            const area = this.getMemberArea(member.profile);
            const volume = area * member.length;
            totalWeight += volume * material.properties.density;
        }

        return Math.round(totalWeight * 10) / 10;
    }

    private getMemberArea(profile: TrussMemberProfile): number {
        // Cross-sectional area in m²
        switch (profile.shape) {
            case 'LShape':
                // Two legs minus overlap
                return ((profile.width + profile.height - profile.thickness!) * profile.thickness!) / 1000000;
            case 'Tube': {
                const outer = profile.width * profile.height;
                const inner = (profile.width - 2 * profile.thickness!) * (profile.height - 2 * profile.thickness!);
                return (outer - inner) / 1000000;
            }
            case 'Pipe': {
                return (Math.PI * Math.pow(profile.width / 2, 2) - Math.PI * Math.pow((profile.width - 2 * profile.thickness!) / 2, 2)) / 1000000;
            }
            case 'WShape':
                // Approximate
                return (profile.width * profile.height * 0.3) / 1000000;
            case 'Timber':
                return (profile.width * profile.height) / 1000000;
            default:
                return (profile.width * profile.height) / 1000000;
        }
    }

    private createAnalyticalModel(members: TrussMember[]): AnalyticalModel {
        const nodeMap = new Map<string, { id: string; position: [number, number, number] }>();

        // Collect unique nodes
        for (const member of members) {
            const startKey = member.start.join(',');
            const endKey = member.end.join(',');

            if (!nodeMap.has(startKey)) {
                nodeMap.set(startKey, { id: uuidv4(), position: member.start });
            }
            if (!nodeMap.has(endKey)) {
                nodeMap.set(endKey, { id: uuidv4(), position: member.end });
            }
        }

        const nodes = Array.from(nodeMap.values()).map(n => ({
            id: n.id,
            position: n.position,
            boundaryConditions: 'Free' as 'Fixed' | 'Pinned' | 'Roller' | 'Free'
        }));

        // Set support conditions
        const sortedNodes = [...nodes].sort((a, b) =>
            a.position[0] - b.position[0] || a.position[1] - b.position[1]
        );
        if (sortedNodes.length >= 2) {
            const startBC: 'Fixed' | 'Pinned' | 'Roller' | 'Free' = this.options.startBearing === 'Fixed' ? 'Fixed' : 'Pinned';
            const endBC: 'Fixed' | 'Pinned' | 'Roller' | 'Free' = this.options.endBearing === 'Roller' ? 'Roller' : 'Pinned';
            sortedNodes[0].boundaryConditions = startBC;
            sortedNodes[sortedNodes.length - 1].boundaryConditions = endBC;
        }

        const curves = members.map(member => {
            const memberType: 'Brace' | 'Beam' = member.type === 'Diagonal' || member.type === 'Vertical' ? 'Brace' : 'Beam';
            return {
                startNodeId: nodeMap.get(member.start.join(','))!.id,
                endNodeId: nodeMap.get(member.end.join(','))!.id,
                type: memberType
            };
        });

        return { nodes, curves };
    }

    private getMaterial(materialId: string): StructuralMaterial {
        const materials: Record<string, StructuralMaterial> = {
            'mat-steel-350': {
                id: 'mat-steel-350',
                name: 'Steel - 350W',
                type: 'Steel',
                properties: {
                    youngsModulus: 200000,
                    shearModulus: 77000,
                    poissonRatio: 0.3,
                    density: 7850,
                    thermalExpansionCoefficient: 0.000012,
                    yieldStrength: 350
                }
            },
            'mat-timber-gl24': {
                id: 'mat-timber-gl24',
                name: 'Glulam GL24h',
                type: 'Wood',
                properties: {
                    youngsModulus: 11500,
                    shearModulus: 720,
                    poissonRatio: 0.35,
                    density: 380,
                    thermalExpansionCoefficient: 0.000005
                }
            }
        };

        return materials[materialId] || materials['mat-steel-350'];
    }

    private buildParameters(geom: EnhancedTrussGeometryData): Record<string, any> {
        return {
            'Truss Type': geom.trussProfile,
            'Span': geom.span,
            'Height': geom.height,
            'Panel Count': geom.panelCount,
            'Panel Width': geom.panelWidth,
            'Top Chord Profile': geom.topChord.profile.size,
            'Bottom Chord Profile': geom.bottomChord.profile.size,
            'Web Profile': geom.webConfig.profile.size,
            'Top Chord Length': geom.topChordLength,
            'Bottom Chord Length': geom.bottomChordLength,
            'Member Count': geom.members.length,
            'Total Weight': geom.totalWeight,
            'Bearing Level': geom.bearingLevelId
        };
    }

    protected buildConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.bearingLevelId,
                data: {
                    offset: this.options.bearingOffset
                }
            }
        ];
    }
}

// ==================== Helper Functions ====================

/**
 * Get available truss profiles
 */
export function getAvailableTrussProfiles(): TrussProfile[] {
    return ['Howe', 'Pratt', 'Warren', 'WarrenWithVerticals', 'Fink', 'KingPost', 'QueenPost', 'Scissors', 'Bowstring', 'FlatTop'];
}

/**
 * Get member schedule from truss
 */
export function getTrussMemberSchedule(truss: EnhancedTrussElement): {
    type: string;
    profile: string;
    quantity: number;
    totalLength: number;
    weight: number;
}[] {
    const members = truss.geometryData.members;
    const schedule = new Map<string, { count: number; length: number }>();

    for (const member of members) {
        const key = `${member.type}|${member.profile.size}`;
        const existing = schedule.get(key) || { count: 0, length: 0 };
        schedule.set(key, {
            count: existing.count + 1,
            length: existing.length + member.length
        });
    }

    return Array.from(schedule.entries()).map(([key, data]) => {
        const [type, profile] = key.split('|');
        const density = 7850; // Steel default
        const area = 0.001; // Approximate
        return {
            type,
            profile,
            quantity: data.count,
            totalLength: Math.round(data.length * 100) / 100,
            weight: Math.round(data.length * area * density * 10) / 10
        };
    });
}

// Export types
